import React from 'react';
import PROJECTS from '../config/projects';

// Command palette (Cmd-K / Ctrl-K / "/") over every section + all 17 projects.
// Quietly discoverable via the header + footer hint lines.

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function jump(hash) {
  const el = document.querySelector(hash);
  if (el) {
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }
}

export default function CommandPalette({ open, setOpen, email, github }) {
  const inputRef = React.useRef(null);
  const [query, setQuery] = React.useState('');
  const [sel, setSel] = React.useState(0);

  const items = React.useMemo(() => {
    const sections = [
      { label: 'How I work', meta: 'section', act: () => jump('#about') },
      { label: 'Projects', meta: 'section', act: () => jump('#work') },
      { label: 'How I use AI tools', meta: 'section', act: () => jump('#ai') },
      { label: 'Background', meta: 'section', act: () => jump('#experience') },
      { label: 'Education', meta: 'section', act: () => jump('#education') },
      {
        label: '/uses · what I work with',
        meta: 'tools',
        act: () => {
          const d = document.getElementById('uses-disclosure');
          if (d) d.open = true;
          jump('#uses');
        },
      },
      { label: 'Certifications', meta: 'section', act: () => jump('#certs') },
      { label: 'Contact', meta: 'section', act: () => jump('#contact') },
      {
        label: 'Email Shaily',
        meta: 'contact',
        act: () => {
          window.location.href = `mailto:${email}`;
        },
      },
      {
        label: 'Open GitHub',
        meta: 'github.com/SHAILY24',
        act: () => window.open(github, '_blank', 'noopener'),
      },
      {
        label: 'Open Upwork',
        meta: 'upwork.com/freelancers/shailydev',
        act: () =>
          window.open(
            'https://www.upwork.com/freelancers/shailydev',
            '_blank',
            'noopener'
          ),
      },
      {
        label: 'Toggle theme',
        meta: 'cli / light',
        act: () => document.body.classList.toggle('cli'),
      },
    ];
    return sections.concat(
      PROJECTS.map((p) => ({
        label: p.name,
        meta: p.live ? `${p.live.replace('https://', '')} · live` : p.band.toLowerCase(),
        act: () =>
          window.open(
            p.live || `https://github.com/${p.repo}`,
            '_blank',
            'noopener'
          ),
      }))
    );
  }, [email, github]);

  const view = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      `${it.label} ${it.meta}`.toLowerCase().includes(q)
    );
  }, [query, items]);

  React.useEffect(() => {
    function onKey(e) {
      const typing = /^(input|textarea)$/i.test(
        (e.target && e.target.tagName) || ''
      );
      if (
        (e.key === '/' && !typing && !open) ||
        ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')
      ) {
        e.preventDefault();
        setOpen(true);
        setQuery('');
        setSel(0);
        return;
      }
      if (!open) return;
      if (e.key === 'Escape') {
        setOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel((s) => Math.min(s + 1, view.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => Math.max(s - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const it = view[sel];
        if (it) {
          setOpen(false);
          it.act();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen, view, sel]);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="pal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="pal">
        <label className="visually-hidden" htmlFor="palInput">
          Jump to a section or project
        </label>
        <input
          id="palInput"
          ref={inputRef}
          type="text"
          placeholder="jump to a project or section…  (esc to close)"
          autoComplete="off"
          spellCheck="false"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSel(0);
          }}
        />
        <ul>
          {view.length ? (
            view.map((it, i) => (
              <li
                key={`${it.label}-${i}`}
                role="option"
                aria-selected={i === sel}
                onMouseEnter={() => setSel(i)}
                onClick={() => {
                  setOpen(false);
                  it.act();
                }}
              >
                <span>{it.label}</span>
                <span className="meta">{it.meta}</span>
              </li>
            ))
          ) : (
            <div className="empty">
              nothing matches that. try "projects" or a repo name.
            </div>
          )}
        </ul>
      </div>
    </div>
  );
}
